import React, { useRef } from 'react';
import { NativeSelect, TextField, TextFieldProps } from '@material-ui/core';
import parsePhoneNumberFromString, {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js/min';

import {
  formatInputField,
  toRegionalIndicators,
} from 'components/inputs/PhoneInput/utils';
import { fakeChangeEvent, prependToInput } from 'utils/dom';

export type Country = CountryCode | 'unknown' | 'blank';
/** Option elements for all the countries known by the lib */
const countryOptions = getCountries().map(c => (
  <option key={c} value={c}>
    {c}: {toRegionalIndicators(c)}
  </option>
));

/**
 * Wrapper around MUI.TextField to input phone numbers
 * Includes country code detection, as well as a dropdown to select the country from a list
 *
 * Formatting currently disabled because it can sometimes cause issues with the cursor getting stuck or jumping unexpectedly
 */
export function PhoneInput({
  value,
  defaultValue,
  onChange,
  country,
  defaultCountry,
  onChangeCountry,
  ...rest
}: Omit<TextFieldProps, 'InputProps' | 'inputProps'> & {
  defaultCountry?: Country;
  country?: Country;
  onChangeCountry?: (c: Country) => void;
  /**
   * The object format is to enable "configuration" of a default country as if it were a default value
   * When such a value is provided, this input will immediately self-change back to string
   */
  value?: string;
  /**
   * The object format is to enable "configuration" of a default country as if it were a default value
   * When such a value is provided, this input will immediately self-change back to string
   */
  defaultValue?: string;
}) {
  const refs = {
    // Used to create fake change events
    hiddenCountry: useRef<HTMLInputElement>(null),
    // Used to create fake change events
    hidden: useRef<HTMLInputElement>(null),
    // Used to focus on the area code if a leading zero is entered and to show/hide the input
    areaCode: useRef<HTMLDivElement>(null),
    // Used to read/write the areacode value / ensure it renders fully
    areaCodeInput: useRef<HTMLSelectElement>(null),
    // Used to maintain cursor position inside input throughout updates and to crop out the area code
    main: useRef<HTMLInputElement>(null),
    // Unknown option (for updating the text based on input)
    unknownOption: useRef<HTMLOptionElement>(null),
  };

  /** Trigger a change on the hidden input */
  const setValue = React.useCallback(
    function setValue(newValue: string) {
      if (refs.hidden.current) {
        fakeChangeEvent(refs.hidden.current, newValue);
      } else {
        console.error(
          'Phone input wanted to create a change event, but the refs.hidden for the hidden input was not available',
          { input: refs.hidden.current, newValue },
        );
      }
    },
    [refs.hidden],
  );
  /** Trigger a change on the country input */
  const setCountryValue = React.useCallback(
    function setValue(newValue: string) {
      if (refs.hiddenCountry.current) {
        fakeChangeEvent(refs.hiddenCountry.current, newValue);
      } else {
        console.error(
          'Phone input wanted to create a change event, but the refs.hiddenCountry for the hidden input was not available',
          { input: refs.hiddenCountry.current, newValue },
        );
      }
    },
    [refs.hiddenCountry],
  );

  const updateDom = React.useCallback(
    function updateDom() {
      const main = refs.main.current;
      const areaCode = refs.areaCode.current;
      const areaCodeInput = refs.areaCodeInput.current;
      const hidden = refs.hidden.current;
      const hiddenCountry = refs.hiddenCountry.current;
      const unknownOption = refs.unknownOption.current;
      if (!main) return;
      if (!areaCode) return;
      if (!areaCodeInput) return;
      if (!hidden) return;
      if (!hiddenCountry) return;
      if (!unknownOption) return;
      const active = document.activeElement === refs.main.current;

      const number = parsePhoneNumberFromString(main.value, {
        extract: false,
      });

      const value = number?.format('E.164') ?? main.value;

      if (value !== hidden.value) {
        setValue(value);
      }

      const [country, selectText] = (function getCCC() {
        // If current input parses to a specific country, use that
        if (number?.country)
          return [number.country, `+${getCountryCallingCode(number.country)}`];
        // Otherwise, if we are already showing a flag that matches the input,
        // keep showing it even if ambiguous
        // (because it was a perfect match earlier, or because the user selected it explicitly)
        if (isSupportedCountry(areaCodeInput.value)) {
          const expected = `+${getCountryCallingCode(areaCodeInput.value)}`;
          if (main.value.startsWith(expected)) {
            return [areaCodeInput.value, expected];
          }
        }

        // Otherwise, try to guess what it might be from the input:
        const candidates = getCountries().filter(country =>
          main.value.startsWith(`+${getCountryCallingCode(country)}`),
        );
        // If there's multiple options, render it as unknown
        if (candidates.length > 1) {
          const expected = `+${getCountryCallingCode(candidates[0])}`;
          return ['unknown', expected];
        }
        // If it has only one option, use that country
        if (candidates.length === 1) {
          const expected = `+${getCountryCallingCode(candidates[0])}`;
          return [candidates[0], expected];
        }
        // If there's no (known) possible country, also show unknown
        if (main.value.match(/^\+\d*$/)) {
          return ['unknown', main.value];
        }
        // Except if there is no + in the first place. In which case it should be blank
        return ['blank', ''];
      })();

      areaCodeInput.value = country;
      if (country !== hiddenCountry.value) {
        setCountryValue(country);
      }
    },
    [
      refs.areaCode,
      refs.areaCodeInput,
      refs.hidden,
      refs.hiddenCountry,
      refs.main,
      refs.unknownOption,
      setCountryValue,
      setValue,
    ],
  );

  // Apply 'country' prop
  React.useEffect(() => {
    if (refs.areaCodeInput.current && country) {
      if (refs.areaCodeInput.current.value !== country) {
        refs.areaCodeInput.current.value = country;
        updateDom();
      }
    }
  }, [refs.areaCodeInput, updateDom, country]);

  // Apply default value(s)
  React.useEffect(() => {
    if (!refs.main.current) return;
    if (!refs.areaCodeInput.current) return;
    if (defaultValue && value === undefined) {
      refs.main.current.value = defaultValue;
    }
    if (defaultCountry && country === undefined) {
      switch (defaultCountry) {
        case 'blank':
          // Ignore provided
          break;
        case 'unknown':
          break;
        default: {
          const valueToCompare = value || defaultValue || '';
          const expected = `+${getCountryCallingCode(defaultCountry)}`;
          if (valueToCompare.startsWith(expected)) {
            refs.areaCodeInput.current.value = defaultCountry;
          } else {
            console.warn(
              'Provided defaultCountry does not match provided value / defaultValue',
            );
          }
        }
      }
    }
    updateDom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onAny() {
    if (refs.main.current) {
      const countryHint = country ?? defaultCountry;
      if (countryHint !== undefined && isSupportedCountry(countryHint)) {
        formatInputField(refs.main.current, countryHint);
      } else {
        formatInputField(refs.main.current);
      }
      updateDom();
    }
  }

  return (
    <>
      <TextField
        {...rest}
        style={{ fontFamily: 'monospace' }}
        inputMode="tel"
        inputRef={refs.main}
        value={value}
        // Typescript issue - Each variant expects its own type specifically, but at this point for all we know it's a union of all three (and undefined)
        variant={rest.variant as any}
        onChange={onAny}
        InputProps={{
          onFocus: onAny,
          onBlur: onAny,
          onSelect: onAny,
          startAdornment: (
            <NativeSelect
              ref={refs.areaCode}
              inputRef={refs.areaCodeInput}
              onFocus={updateDom}
              value={country}
              defaultValue={defaultCountry ?? 'unknown'}
              variant="filled"
              style={{
                alignSelf: 'stretch',
                flexShrink: 0,
              }}
              inputProps={{
                style: {
                  // Simplifies alignment for the 'unknown' input, this way it doesn't need to be padded to 11 characters
                  textAlign: 'end',
                  // Ensures alignment is consistent between all the options
                  fontFamily: 'monospace',
                },
              }}
              onChange={e => {
                if (!refs.main.current) return;
                const value = e.target.value as string;
                if (value === 'blank') {
                  const number = parsePhoneNumberFromString(
                    refs.main.current.value,
                  );
                  const ccc = number?.countryCallingCode;
                  if (ccc) {
                    refs.main.current.value = refs.main.current.value.replace(
                      `+${ccc}`,
                      '',
                    );
                  } else {
                    refs.main.current.value = '';
                  }
                } else if (isSupportedCountry(value)) {
                  const number = parsePhoneNumberFromString(
                    refs.main.current.value,
                  );
                  const ccc = getCountryCallingCode(value);
                  if (number) {
                    refs.main.current.value = `+${ccc}${refs.main.current.value.replace(
                      `+${number.countryCallingCode}`,
                      '',
                    )}`;
                  } else {
                    prependToInput(refs.main.current, `+${ccc}`);
                  }
                }
                onAny();
              }}
            >
              {countryOptions}
              <option disabled value="unknown" ref={refs.unknownOption}>
                {/* Text content updated dynamically */ '    🌐'}
              </option>
              <option value="blank"> </option>
            </NativeSelect>
          ),
          inputProps: {
            type: 'tel', // Might as well
            style: {
              // Some custom fonts break the negative text indent used to collapse the area code into the select
              // As long as the font is monospace, the alignment should work fine
              fontFamily: 'monospace',
            },
          },
          // Use an invisible input to generate the necessary 'change' events
          endAdornment: (
            <>
              <input
                style={{ display: 'none' }}
                ref={refs.hidden}
                name={rest.name} // Some forms need the name attribute to distinguish change sources
                onChange={onChange}
              />
              <input
                style={{ display: 'none' }}
                ref={refs.hiddenCountry}
                onChange={e => onChangeCountry?.(e.target.value as CountryCode)}
              />
            </>
          ),
        }}
      />
    </>
  );
}
